import { JATOTheme } from '@jato/ui-component-library'
import { StyleSheet } from '@react-pdf/renderer'
import { ContentParts } from 'models/News/Article/NewsArticle'
import { NewsCategoryType } from 'models/News/NewsCategoryType'
import { ArticleContentType } from './newsHelper'

import NotoSerifJP from 'assets/fonts/NotoSerifJP-Regular.otf'
import NotoSerifSC from 'assets/fonts/NotoSerifSC-Regular.otf'
import Roboto from 'assets/fonts/Roboto-Regular.ttf'

import NotoSansArabic from 'assets/fonts/NotoSansArabic-VariableFont_wdth,wght.ttf'
import { NewsArticleResponse } from 'models/News/Article/NewsArticleResponse'
import { NewsArticleRequest } from 'models/News/Homepage/NewsArticleRequest'
import newsService from 'services/News/NewsService'

export const updateContentWithPng = async (
  content: ContentParts
): Promise<ContentParts> => {
  let newImage = content.image
  if (content.type === ArticleContentType.Image) {
    const jimpImage = await window.Jimp.read(
      `data:image/gif;base64,${content.image}`
    )
    newImage = await jimpImage.getBase64Async(window.Jimp.MIME_PNG)
  }

  const updateContentPart = { ...content, image: newImage }
  return updateContentPart
}

export const fetchAllUpdatedContentPart = async (
  contents: ContentParts[]
): Promise<ContentParts[]> =>
  Promise.all(
    contents.map((content: ContentParts) => updateContentWithPng(content))
  )

export const fetchAllArticles = async (
  newsArticleRequests: NewsArticleRequest[]
): Promise<NewsArticleResponse[]> =>
  Promise.all(
    newsArticleRequests.map((newsArticleRequest: NewsArticleRequest) =>
      newsService.getArticleDetailByArticleId(newsArticleRequest)
    )
  )

export const loadFont = (languageId: number): any => {
  switch (languageId) {
    case 44:
      return { src: NotoSansArabic }
    case 39:
      return { src: NotoSerifSC }
    case 8:
      return { src: NotoSerifJP }
    default:
      return { src: Roboto }
  }
}

// Create styles
export const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
  },
  categorySection: {
    display: 'flex',
    marginLeft: 10,
    padding: 10,
    flexDirection: 'row',
    gap: '5px',
  },
  headerSection: {
    margin: 10,
    padding: 10,
  },
  metaDataSection: {
    display: 'flex',
    marginLeft: 10,
    padding: 10,
    flexDirection: 'row',
    gap: '10px',
  },
  contentSection: {
    paddingRight: 50,
    paddingLeft: 20,
    fontFamily: 'Roboto',
    fontWeight: 300,
  },
  '.table': {
    fontFamily: 'Roboto',
    fontSize: '10px',
    fontWeight: 300,
    letterSpacing: '0px',
    textAlign: 'left',
    borderCollase: 'collapse',
    paddingTop: '20px',
    paddingBottom: '20px',
    borderSpacing: 0,
  },
  '.table th': {
    fontWeight: 700,
    paddingTop: '12px',
    paddingBottom: '12px',
    textAlign: 'left',
    border: '1px solid #ddd',
    padding: '8px',
  },
  '.table td': {
    border: '1px solid #ddd',
    padding: '8px',
  },
  '.table tr:nth-child(even)': {
    backgroundColor: '#fcfcfc',
  },
  '.incentives_daily_table, .incentives_event_table,.incentives_table': {
    width: '100%',
    borderSpacing: 0,
    borderCollapse: 'collapse',
    backgroundColor: 'transparent',
  },
  '.incentives_daily_row_center_content': {
    borderRight: '1px solid #ddd',
    borderTop: '1px solid #ddd',
    borderLeft: '1px solid #ddd',
    borderBottom: '1px solid #ddd',
    textAlign: 'center',
    fontFamily: 'Roboto',
    fontSize: '12px',
    fontWeight: 300,
    letterSpacing: '0px',
    lineHeight: '3px',
    verticalAlign: 'super',
  },
  '.incentives_daily_row_left_content': {
    borderRight: '1px solid #ddd',
    borderTop: '1px solid #ddd',
    fontWeight: 300,
    borderLeft: '1px solid #ddd',
    borderBottom: '1px solid #ddd',
    fontFamily: 'Roboto',
    textAlign: 'center',
  },
  '.incentives_daily_model': {
    borderRight: '1px solid #ddd',
    borderTop: '1px solid #ddd',
    fontWeight: 300,
    borderLeft: '1px solid #ddd',
    borderBottom: '1px solid #ddd',
    fontFamily: 'Roboto',
    backgroundColor: `${JATOTheme.colors.midGrey}`,
    textAlign: 'center',
  },
  '.incentives_daily_row_center_heading': {
    borderRight: '1px solid #ddd',
    borderTop: '1px solid #ddd',
    fontWeight: 300,
    borderLeft: '1px solid #ddd',
    borderBottom: '1px solid #ddd',
    fontFamily: 'Roboto',
    backgroundColor: `${JATOTheme.colors.midGrey}`,
    textAlign: 'center',
  },
  '.incentives_daily_row_left_heading': {
    borderRight: '1px solid #ddd',
    borderTop: '1px solid #ddd',
    fontWeight: 300,
    borderLeft: '1px solid #ddd',
    width: '20%',
    borderBottom: '1px solid #ddd',
    fontFamily: 'Roboto',
    backgroundColor: `${JATOTheme.colors.midGrey}`,
    textAlign: 'center',
  },
  newsMetadataBlock: {
    width: '170px',
    borderRadius: '100px',
    boxShadow: `${JATOTheme.shadows[4]}`,
    backgroundColor: `${JATOTheme.colors.midGrey}`,
    fontFamily: 'Roboto',
    fontSize: '12px',
    fontWeight: 500,
    display: 'flex',
    gap: '5px',
    padding: '5px 10px 5px 20px',
  },
  '.mp-basic-article-header': {
    fontSize: '14px',
    lineHeight: '1.5',
    letterSpacing: '1px',
    boxSizing: 'border-box',
    tableLayout: 'fixed',
    width: '100%',
    border: 'none',
    paddingBottom: '10px',
  },
  '.mp-basic-article-header td, .mp-basic-article-header th': {
    border: 'none',
    paddingTop: '12px',
    paddingBottom: '12px',
  },
  '.mp-basic-article-table': {
    fontSize: '24px',
    lineHeight: '1',
    letterSpacing: '1px',
    boxSizing: 'border-box',
    tableLayout: 'fixed',
    width: '100%',
    borderSpacing: 'unset',
    border: '1px solid #d9d9d9',
  },
  '.mp-basic-article-table td': {
    border: 'none',
    fontSize: '10px',
    textAlign: 'center',
    padding: '8px 8px',
    borderBottom: '1px solid #d9d9d9',
  },
  '.mp-basic-article-table th': {
    border: 'none',
    borderBottom: '1px solid #d9d9d9',
    padding: '8px',
  },
  '.mp-basic-article-table tr:first-child': {
    backgroundColor: '#f0f0f0',
    fontSize: '12px',
  },
  '.mp-basic-article-table tr:nth-child(even)': {
    backgroundColor: '#fcfcfc',
  },
  '.mp-basic-article-table th:nth-child(1)': {
    textAlign: 'left',
    fontSize: '12px',
    borderLeft: '1px solid #d9d9d9',
  },
  '.legend-increase': {
    color: 'white', // display:none does not work
  },
  '.legend-decrease': {
    color: 'white', // display:none does not work
  },
})

export const pdfSvgs: any = {
  [NewsCategoryType.Volumes]: {
    viewBox: '0 0 24 24',
    paths: [
      {
        d: 'M1.66667 12.9013V14.1543C1.66667 14.3516 1.60677 14.5169 1.48698 14.6503C1.36719 14.7837 1.21875 14.8504 1.04167 14.8504H0.625C0.447917 14.8504 0.299479 14.7837 0.179688 14.6503C0.0598958 14.5169 0 14.3516 0 14.1543V6.63629L1.77083 0.696114C1.8125 0.479545 1.91944 0.309384 2.09167 0.185631C2.26389 0.0618768 2.45556 0 2.66667 0H12.3333C12.5444 0 12.7361 0.0618768 12.9083 0.185631C13.0806 0.309384 13.1875 0.479545 13.2292 0.696114L15 6.63629V14.1543C15 14.3516 14.9401 14.5169 14.8203 14.6503C14.7005 14.7837 14.5521 14.8504 14.375 14.8504H13.9375C13.7604 14.8504 13.612 14.7837 13.4922 14.6503C13.3724 14.5169 13.3125 14.3516 13.3125 14.1543V12.9013H1.66667ZM1.72917 5.24406H13.2708L12.125 1.39223H2.875L1.72917 5.24406ZM3.45344 10.3489C3.77615 10.3489 4.04514 10.2271 4.26042 9.98344C4.47569 9.7398 4.58333 9.44395 4.58333 9.09589C4.58333 8.73495 4.47396 8.42814 4.25521 8.17547C4.03646 7.92281 3.77083 7.79648 3.45833 7.79648C3.13426 7.79648 2.8588 7.92228 2.63194 8.17389C2.40509 8.42548 2.29167 8.731 2.29167 9.09044C2.29167 9.44987 2.40462 9.74947 2.63052 9.98924C2.85641 10.229 3.13072 10.3489 3.45344 10.3489ZM11.5625 10.3489C11.8866 10.3489 12.162 10.2271 12.3889 9.98344C12.6157 9.7398 12.7292 9.44395 12.7292 9.09589C12.7292 8.73495 12.6162 8.42814 12.3903 8.17547C12.1644 7.92281 11.8901 7.79648 11.5674 7.79648C11.2447 7.79648 10.9757 7.92228 10.7604 8.17389C10.5451 8.42548 10.4375 8.731 10.4375 9.09044C10.4375 9.44987 10.5469 9.74947 10.7656 9.98924C10.9844 10.229 11.25 10.3489 11.5625 10.3489ZM8.25 20.4194L3.58333 17.6813H6.66667V15.9178L11.4167 18.563H8.25V20.4194ZM1.25 11.5091H13.75V6.63629H1.25V11.5091Z',
        fill: '#454545',
      },
    ],
  },
  [NewsCategoryType.PricingAndSpecifications]: {
    viewBox: '0 0 24 24',
    paths: [
      {
        d: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z',
        fill: '#454545',
      },
      {
        d: 'M0 0h24v24H0z',
        fill: 'none',
      },
    ],
  },
  [NewsCategoryType.NewVehicles]: {
    viewBox: '0 0 24 24',
    paths: [
      {
        d: 'M12.0249 23.2749L8.6499 19.9999H3.9999V15.3499L0.649902 11.9999L3.9999 8.6499V3.9999H8.6499L12.0249 0.649902L15.3499 3.9999H19.9999V8.6499L23.3499 11.9999L19.9999 15.3499V19.9999H15.3499L12.0249 23.2749ZM12.0249 21.1749L14.7228 18.4999H18.4999V14.7249L21.2249 11.9999L18.4999 9.2749V5.4999H14.7249L12.0249 2.7749L9.2749 5.4999H5.4999V9.2749L2.7749 11.9999L5.4999 14.7249V18.4999H9.2499L12.0249 21.1749ZM9.0749 16.2499L11.9999 14.4749L14.9249 16.2499L14.1499 12.9249L16.7499 10.6749L13.3249 10.3999L11.9999 7.2499L10.6749 10.3999L7.2499 10.6749L9.8499 12.9249L9.0749 16.2499Z',
        fill: '#454545',
      },
    ],
  },
  [NewsCategoryType.IndustryNews]: {
    viewBox: '0 0 24 24',
    paths: [
      {
        d: 'M19.8183 16H5.89786V17C5.89786 17.2652 5.7931 17.5196 5.60663 17.7071C5.42016 17.8946 5.16725 18 4.90354 18H3.90922C3.64551 18 3.39261 17.8946 3.20613 17.7071C3.01966 17.5196 2.91491 17.2652 2.91491 17V9.5L1.67897 9.19C1.46395 9.13583 1.2731 9.01095 1.13672 8.83521C1.00035 8.65946 0.926275 8.44293 0.92627 8.22L0.92627 7.5C0.92627 7.36739 0.978649 7.24022 1.07188 7.14645C1.16512 7.05268 1.29157 7 1.42343 7H2.91491L5.38082 1.212C5.53426 0.852001 5.78944 0.545239 6.11468 0.32978C6.43992 0.114322 6.8209 -0.000347568 7.21036 7.91365e-07H18.5058C18.8949 4.4012e-05 19.2755 0.114897 19.6003 0.330331C19.9252 0.545765 20.1801 0.852314 20.3334 1.212L22.8013 7H24.2927C24.4246 7 24.5511 7.05268 24.6443 7.14645C24.7375 7.24022 24.7899 7.36739 24.7899 7.5V8.22C24.7899 8.44293 24.7158 8.65946 24.5795 8.83521C24.4431 9.01095 24.2522 9.13583 24.0372 9.19L22.8013 9.5V17C22.8013 17.2652 22.6965 17.5196 22.51 17.7071C22.3236 17.8946 22.0707 18 21.807 18H20.8126C20.5489 18 20.296 17.8946 20.1095 17.7071C19.9231 17.5196 19.8183 17.2652 19.8183 17V16ZM20.8126 14V9H4.90354V14H20.8126ZM6.37215 7H19.344C19.5064 6.9999 19.6663 6.95979 19.8098 6.88318C19.9532 6.80657 20.0758 6.6958 20.1668 6.56054C20.2579 6.42528 20.3146 6.26966 20.332 6.10727C20.3494 5.94488 20.327 5.78067 20.2668 5.629L18.824 2H6.89218L5.44942 5.629C5.38917 5.78067 5.36678 5.94488 5.3842 6.10727C5.40162 6.26966 5.45832 6.42528 5.54936 6.56054C5.64039 6.6958 5.76298 6.80657 5.90641 6.88318C6.04984 6.95979 6.20975 6.9999 6.37215 7ZM5.89786 10C8.2017 10 9.75482 10.755 10.5572 12.264C10.5978 12.3402 10.618 12.4257 10.616 12.5121C10.6139 12.5985 10.5896 12.6829 10.5454 12.757C10.5012 12.8311 10.4387 12.8925 10.3639 12.9351C10.2892 12.9777 10.2047 13 10.1187 13H6.89218C6.62847 13 6.37556 12.8946 6.18909 12.7071C6.00262 12.5196 5.89786 12.2652 5.89786 12V10ZM19.8183 10V12C19.8183 12.2652 19.7136 12.5196 19.5271 12.7071C19.3406 12.8946 19.0877 13 18.824 13H15.5974C15.5116 12.9999 15.4272 12.9774 15.3526 12.9347C15.2779 12.892 15.2156 12.8307 15.1715 12.7566C15.1275 12.6825 15.1032 12.5981 15.1012 12.5118C15.0992 12.4255 15.1194 12.3401 15.1599 12.264C15.9604 10.754 17.5135 10 19.8183 10Z',
        fill: '#454545',
      },
    ],
  },
  [NewsCategoryType.PromotionsAndIncentives]: {
    viewBox: '0 0 24 24',
    paths: [
      {
        fillRule: 'evenodd',
        d: 'M10.8411 20C16.3326 20 20.7843 15.5228 20.7843 10C20.7843 4.47715 16.3326 0 10.8411 0C5.34966 0 0.897949 4.47715 0.897949 10C0.897949 15.5228 5.34966 20 10.8411 20ZM10.8411 18.5C15.5089 18.5 19.2928 14.6944 19.2928 10C19.2928 5.30558 15.5089 1.5 10.8411 1.5C6.17338 1.5 2.38943 5.30558 2.38943 10C2.38943 14.6944 6.17338 18.5 10.8411 18.5Z',
        fill: '#444444',
      },
      {
        d: 'M10.375 17.1484V15.7422C9.67591 15.6536 9.10625 15.4974 8.66606 15.2734C8.23105 15.0443 7.853 14.6771 7.53192 14.1719C7.21601 13.6667 7.03217 13.0495 6.98038 12.3203L8.38641 12.0547C8.49516 12.8099 8.68678 13.3646 8.96125 13.7188C9.35483 14.2188 9.8261 14.4974 10.375 14.5547V10.0781C9.8002 9.96875 9.21242 9.74479 8.61168 9.40625C8.16631 9.15625 7.82193 8.8099 7.57852 8.36719C7.3403 7.92448 7.22119 7.42188 7.22119 6.85938C7.22119 5.85938 7.57335 5.04948 8.27765 4.42969C8.74892 4.01302 9.44805 3.75781 10.375 3.66406V2.99219H11.1985V3.66406C12.0115 3.74219 12.6563 3.98177 13.1327 4.38281C13.7438 4.89323 14.1115 5.59375 14.2358 6.48438L12.7909 6.70312C12.7081 6.15104 12.5346 5.72917 12.2705 5.4375C12.0115 5.14062 11.6542 4.94531 11.1985 4.85156V8.90625C11.9028 9.08333 12.3689 9.22135 12.5967 9.32031C13.0317 9.51302 13.3865 9.7474 13.661 10.0234C13.9354 10.2995 14.1452 10.6276 14.2902 11.0078C14.4404 11.388 14.5154 11.7995 14.5154 12.2422C14.5154 13.2161 14.2073 14.0286 13.591 14.6797C12.9748 15.3307 12.1772 15.6797 11.1985 15.7266V17.1484H10.375ZM10.375 4.83594C9.83128 4.91927 9.40144 5.13802 9.08554 5.49219C8.77481 5.84635 8.61945 6.26562 8.61945 6.75C8.61945 7.22917 8.75151 7.63021 9.01562 7.95312C9.28492 8.27604 9.73806 8.53385 10.375 8.72656V4.83594ZM11.1985 14.5547C11.7422 14.487 12.1902 14.25 12.5423 13.8438C12.8997 13.4375 13.0783 12.9349 13.0783 12.3359C13.0783 11.8255 12.9515 11.4167 12.6977 11.1094C12.4491 10.7969 11.9494 10.5182 11.1985 10.2734V14.5547Z',
        fill: '#444444',
      },
    ],
  },
  [NewsCategoryType.EditorsChoice]: {
    viewBox: '0 0 24 24',
    paths: [
      {
        d: 'M12.0249 23.2749L8.6499 19.9999H3.9999V15.3499L0.649902 11.9999L3.9999 8.6499V3.9999H8.6499L12.0249 0.649902L15.3499 3.9999H19.9999V8.6499L23.3499 11.9999L19.9999 15.3499V19.9999H15.3499L12.0249 23.2749ZM12.0249 21.1749L14.7228 18.4999H18.4999V14.7249L21.2249 11.9999L18.4999 9.2749V5.4999H14.7249L12.0249 2.7749L9.2749 5.4999H5.4999V9.2749L2.7749 11.9999L5.4999 14.7249V18.4999H9.2499L12.0249 21.1749ZM9.0749 16.2499L11.9999 14.4749L14.9249 16.2499L14.1499 12.9249L16.7499 10.6749L13.3249 10.3999L11.9999 7.2499L10.6749 10.3999L7.2499 10.6749L9.8499 12.9249L9.0749 16.2499Z',
        fill: '#454545',
      },
    ],
  },
  [NewsCategoryType.MonthlyPayments]: {
    viewBox: '0 0 24 24',
    paths: [
      {
        d: 'M15.0189 22.3519C15.0189 23.2629 15.7462 24 16.6451 24C17.5441 24 18.2714 23.2629 18.2714 22.3519H15.0189ZM16.6451 9.92048C18.9006 9.92048 20.7312 11.7757 20.7312 14.0615V19.859H12.5591V14.0615C12.5591 11.7757 14.3896 9.92048 16.6451 9.92048ZM16.6451 6.19354C15.9669 6.19354 15.4193 6.74844 15.4193 7.43585V8.40486C12.8533 8.96804 10.9247 11.287 10.9247 14.0615V19.0308L9.29028 20.6872V21.5154H24V20.6872L22.3656 19.0308V14.0615C22.3656 11.287 20.437 8.96804 17.8709 8.40486V7.43585C17.8709 6.74844 17.3234 6.19354 16.6451 6.19354Z',
        fill: '#323232',
      },
      {
        d: 'M18.8129 0H2.09033C0.940647 0 0 0.958066 0 2.12904V14.9033C0 16.0742 0.940647 17.0323 2.09033 17.0323H9.78496V14.9033H2.09033V4.25807L10.4516 9.58066L18.8129 4.25807V7.45163L20.9033 8.229V2.12904C20.9033 0.958066 19.9626 0 18.8129 0ZM10.4516 7.45163L2.09033 2.12904H18.8129L10.4516 7.45163Z',
        fill: '#323232',
      },
    ],
  },
}
